import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import UploadsHandler from './components/UploadsHandler';
import ImageViewer from './components/ImageViewer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import ClientList from './pages/ClientList';
import ClientForm from './pages/ClientForm';
import ClientDetail from './pages/ClientDetail';
import AssetList from './pages/AssetList';
import AssetForm from './pages/AssetForm';
import AssetDetail from './pages/AssetDetail';
import CategoryList from './pages/CategoryList';
import ServiceRequestKanban from './pages/ServiceRequestKanban';
import ServiceRequestForm from './pages/ServiceRequestForm';
import ServiceRequestDetail from './pages/ServiceRequestDetail';
import ServiceRequestList from './pages/ServiceRequestList';
import UserList from './pages/UserList';
import UserForm from './pages/UserForm';
import UserDetail from './pages/UserDetail';
import SLAList from './pages/SLAList';
import SLAForm from './pages/SLAForm';
import ChecklistTemplateList from './pages/ChecklistTemplateList';
import ChecklistTemplateForm from './pages/ChecklistTemplateForm';
import CostList from './pages/CostList';
import CostForm from './pages/CostForm';
import CostDetail from './pages/CostDetail';
import CommercialVisitList from './pages/CommercialVisitList';
import CommercialVisitForm from './pages/CommercialVisitForm';
import CommercialVisitDetail from './pages/CommercialVisitDetail';
import FinancialDashboard from './pages/FinancialDashboard';
import RouteList from './pages/RouteList';
import RouteOptimization from './pages/RouteOptimization';
import RouteDetail from './pages/RouteDetail';
import Calendar from './pages/Calendar';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Create a responsive theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#2e7d32', // Green color for primary elements
      light: '#60ad5e',
      dark: '#005005',
    },
    secondary: {
      main: '#f57c00', // Orange color for secondary elements
      light: '#ffad42',
      dark: '#bb4d00',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '2rem',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontSize: '1.25rem',
      '@media (max-width:600px)': {
        fontSize: '1.1rem',
      },
    },
    h6: {
      fontSize: '1.1rem',
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
    },
    body1: {
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.95rem',
      },
    },
    body2: {
      fontSize: '0.875rem',
      '@media (max-width:600px)': {
        fontSize: '0.85rem',
      },
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
        },
        sizeSmall: {
          padding: '4px 8px',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
          '@media (max-width:600px)': {
            padding: '6px 8px',
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          '@media (max-width:600px)': {
            padding: '4px 6px',
          },
        },
      },
    },
  },
});

const App: React.FC = () => {
  // Definição do ProtectedRoute movida para dentro do componente App
  const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser, loading } = useAuth();

    if (loading) {
      return <div>Loading...</div>;
    }

    if (!currentUser) {
      return <Navigate to="/login" />;
    }

    return <>{children}</>;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SnackbarProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/clients" 
              element={
                <ProtectedRoute>
                  <ClientList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/clients/new" 
              element={
                <ProtectedRoute>
                  <ClientForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/clients/edit/:clientId" 
              element={
                <ProtectedRoute>
                  <ClientForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/clients/:clientId" 
              element={
                <ProtectedRoute>
                  <ClientDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assets" 
              element={
                <ProtectedRoute>
                  <AssetList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assets/new" 
              element={
                <ProtectedRoute>
                  <AssetForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assets/edit/:assetId" 
              element={
                <ProtectedRoute>
                  <AssetForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assets/:assetId" 
              element={
                <ProtectedRoute>
                  <AssetDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/categories" 
              element={
                <ProtectedRoute>
                  <CategoryList />
                </ProtectedRoute>
              } 
            />
            
            {/* Service Request Routes */}
            <Route 
              path="/service-requests" 
              element={
                <ProtectedRoute>
                  <ServiceRequestList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/service-requests/kanban" 
              element={
                <ProtectedRoute>
                  <ServiceRequestKanban />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/service-requests/new" 
              element={
                <ProtectedRoute>
                  <ServiceRequestForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/service-requests/edit/:id" 
              element={
                <ProtectedRoute>
                  <ServiceRequestForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/service-requests/:id" 
              element={
                <ProtectedRoute>
                  <ServiceRequestDetail />
                </ProtectedRoute>
              } 
            />
            
            {/* User Management Routes */}
            <Route 
              path="/users" 
              element={
                <ProtectedRoute>
                  <UserList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users/new" 
              element={
                <ProtectedRoute>
                  <UserForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users/edit/:id" 
              element={
                <ProtectedRoute>
                  <UserForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users/:userId" 
            element={
              <ProtectedRoute>
                <UserDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/slas" 
            element={
              <ProtectedRoute>
                <SLAList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/slas/new" 
            element={
              <ProtectedRoute>
                <SLAForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/slas/:slaId" 
            element={
              <ProtectedRoute>
                <SLAForm />
              </ProtectedRoute>
            } 
          />
          {/* Checklist Template Routes */}
          <Route 
            path="/checklist-templates" 
            element={
              <ProtectedRoute>
                <ChecklistTemplateList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/checklist-templates/new" 
            element={
              <ProtectedRoute>
                <ChecklistTemplateForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/checklist-templates/:id" 
            element={
              <ProtectedRoute>
                <ChecklistTemplateForm />
              </ProtectedRoute>
            } 
          />
          {/* Cost Routes */}
          <Route 
            path="/costs" 
            element={
              <ProtectedRoute>
                <CostList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/costs/new" 
            element={
              <ProtectedRoute>
                <CostForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/costs/edit/:id" 
            element={
              <ProtectedRoute>
                <CostForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/costs/:id" 
            element={
              <ProtectedRoute>
                <CostDetail />
              </ProtectedRoute>
            } 
          />
          {/* Commercial Visit Routes */}
          <Route 
            path="/commercial-visits" 
            element={
              <ProtectedRoute>
                <CommercialVisitList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/commercial-visits/new" 
            element={
              <ProtectedRoute>
                <CommercialVisitForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/commercial-visits/:id/edit" 
            element={
              <ProtectedRoute>
                <CommercialVisitForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/commercial-visits/:id" 
            element={
              <ProtectedRoute>
                <CommercialVisitDetail />
              </ProtectedRoute>
            } 
          />
          {/* Financial Dashboard */}
          <Route 
            path="/financial-dashboard" 
            element={
              <ProtectedRoute>
                <FinancialDashboard />
              </ProtectedRoute>
            } 
          />
          {/* Route Planning Routes */}
          <Route 
            path="/routes" 
            element={
              <ProtectedRoute>
                <RouteList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/routes/optimization" 
            element={
              <ProtectedRoute>
                <RouteOptimization />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/routes/:id" 
            element={
              <ProtectedRoute>
                <RouteDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/calendar" 
            element={
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            } 
          />
          {/* Rota para arquivos de upload - NÃO redirecionar para o dashboard */}
          <Route path="/uploads/*" element={<UploadsHandler />} />
          <Route path="/view-image" element={<ImageViewer />} />
          
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
        </SnackbarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
